/**
 * Detects the dominant non-Latin script in a string and returns the appropriate BCP 47 language tag.
 * Returns undefined if the text is primarily Latin or undetectable.
 *
 * Used to set the `lang` attribute on elements containing non-English text
 * so screen readers can switch pronunciation (WCAG 3.1.2).
 */
export const detectLang = (text: string): string | undefined => {
    if (!text) return undefined;

    // Count characters in each script range
    let cjk = 0;
    let hangul = 0;
    let kana = 0;
    let arabic = 0;
    let devanagari = 0;
    let thai = 0;
    let cyrillic = 0;
    let hebrew = 0;

    for (const ch of text) {
        const code = ch.codePointAt(0)!;
        if (code >= 0x4E00 && code <= 0x9FFF || code >= 0x3400 && code <= 0x4DBF || code >= 0x20000 && code <= 0x2A6DF) {
            cjk++;
        } else if (code >= 0xAC00 && code <= 0xD7AF || code >= 0x1100 && code <= 0x11FF) {
            hangul++;
        } else if (code >= 0x3040 && code <= 0x309F || code >= 0x30A0 && code <= 0x30FF) {
            kana++;
        } else if (code >= 0x0600 && code <= 0x06FF) {
            arabic++;
        } else if (code >= 0x0900 && code <= 0x097F) {
            devanagari++;
        } else if (code >= 0x0E00 && code <= 0x0E7F) {
            thai++;
        } else if (code >= 0x0400 && code <= 0x04FF) {
            cyrillic++;
        } else if (code >= 0x0590 && code <= 0x05FF) {
            hebrew++;
        }
    }

    const max = Math.max(cjk, hangul, kana, arabic, devanagari, thai, cyrillic, hebrew);
    if (max === 0) return undefined;

    // If CJK characters dominate but kana is also present, it's likely Japanese
    if (kana > 0 && kana + cjk > Math.max(hangul, arabic, devanagari, thai, cyrillic, hebrew)) return 'ja';
    if (hangul === max) return 'ko';
    if (cjk === max) return 'zh';
    if (kana === max) return 'ja';
    if (arabic === max) return 'und-Arab';
    if (devanagari === max) return 'und-Deva';
    if (thai === max) return 'th';
    if (cyrillic === max) return 'und-Cyrl';
    if (hebrew === max) return 'he';

    return undefined;
};
